/**
 * Settings Cache Service
 * Caches settings documents from Firestore to avoid repeated DB calls
 */

import { collection, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase.config';
import { getSettingsPath } from '../utils/dbPaths';
import { useSettings } from '../stores';
import { isOnline } from './networkStatus';

// localStorage caching removed - using Firestore persistent cache only

/**
 * Initialize settings cache - sets up real-time listener for settings
 * @returns {Function} Unsubscribe function
 */
export const initializeSettingsCache = () => {
  const settingsPath = getSettingsPath();
  
  if (!settingsPath || !settingsPath.includes('tenants/')) {
    console.warn('⚠️ Cannot initialize settings cache: No tenant/store context');
    return () => {};
  }

  // Firestore persistent cache handles offline data automatically

  console.log(`🏪 Initializing settings cache with real-time listener: ${settingsPath}`);

  const settingsRef = collection(db, settingsPath);

  const unsubscribe = onSnapshot(
    settingsRef,
    (snapshot) => {
      const settings = {};
      snapshot.forEach((doc) => {
        settings[doc.id] = doc.data();
      });

      console.log(`✅ Settings updated in Zustand store: ${Object.keys(settings).length} setting(s)`);

      // Store entire settings object in Zustand
      const setSettings = useSettings.getState().setSettings;
      setSettings(settings);
    },
    (error) => {
      console.error('❌ Error in settings subscription:', error);
    }
  );

  return unsubscribe;
};

/**
 * Get a setting from cache or Firestore
 * @param {string} settingName - Setting document name
 * @param {string} fieldName - Field name within the document
 * @param {*} defaultValue - Default value if not found
 * @returns {Promise<*>} Setting value
 */
export const getSetting = async (settingName, fieldName, defaultValue = null) => {
  const cacheKey = `${settingName}.${fieldName}`;
  
  // Check Zustand store first
  const settings = useSettings.getState().settings;
  if (settings[settingName] && settings[settingName][fieldName] !== undefined) {
    return settings[settingName][fieldName];
  }
  
  // Not in store - fetch from Firestore
  try {
    const primaryPath = getSettingsPath();
    const tryPaths = [primaryPath];

    // Backward compatibility: if primary path is tenant-scoped, also try root 'settings'
    if (primaryPath !== "settings") {
      tryPaths.push("settings");
    }

    for (const path of tryPaths) {
      const settingDoc = await getDoc(doc(db, path, settingName));
      if (settingDoc.exists()) {
        const data = settingDoc.data();
        const value =
          data[fieldName] !== undefined ? data[fieldName] : defaultValue;

        // Update Zustand store with this setting
        const updateSetting = useSettings.getState().updateSetting;
        if (!settings[settingName]) {
          settings[settingName] = {};
        }
        settings[settingName][fieldName] = value;
        // Store the full document in settings object
        const setSettings = useSettings.getState().setSettings;
        setSettings({ ...settings, [settingName]: settingDoc.data() });

        return value;
      }
    }

    // If not found in any path, return default
    return defaultValue;
  } catch (error) {
    console.error(`❌ Error loading setting ${settingName}.${fieldName}:`, error);
    return defaultValue;
  }
};

/**
 * Clear all cached settings
 */
export const clearSettingsCache = () => {
  try {
    const clearSettings = useSettings.getState().clearSettings;
    clearSettings();
  } catch (error) {
    console.error('❌ Error clearing settings cache:', error);
  }
};

/**
 * Bulk load multiple settings at once
 * @param {Array<{name: string, field: string, defaultValue: *}>} settingsArray
 * @returns {Promise<Object>} Object with setting values
 */
export const getSettings = async (settingsArray) => {
  const results = {};
  
  await Promise.all(
    settingsArray.map(async ({ name, field, defaultValue }) => {
      const value = await getSetting(name, field, defaultValue);
      results[`${name}_${field}`] = value;
    })
  );
  
  return results;
};
